import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import * as EditService from '@/api/orderEdit';
import { EditRentRoomInfo } from '@/redux/reducers/reservation/base';
import { findItemByKey, flatPriceList } from '@/redux/actions/reservation/helper';
import { ReducerState, MainState } from '@/redux/reducers/reservation/reserveEditor';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';
import getRandomId from '@/utils/uuid';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';

export const getStateFromOrder: (recNos: string[]) => Promise<MainState> = async (recNos) => {
  const res = await EditService.getReserveOrderDetail({ recNos });
  const {
    occType,
    expectedArrivalTime,
    occDuration,
    expectedDepartureTime,

    priceChannel,
    sourceChannelId,
    sourceChannelName,
    priceStrategyId,
    priceStrategyName,
    channelOrderNo,

    remark,
    checkinRemark,
    // memberFlag,
    memberLevel,
    wyuid,
    resvNo,
    subOrderCreateVOS,
  } = res.data;

  // 入住日期索引
  const rentDateIdMap = {};
  // 原始券列表
  const originCouponList = [];
  // 原始门市价
  const originMarketPriceMap = {};

  const rentRoomInfos = (subOrderCreateVOS || []).map((roomItem) => {
    const { recNo } = roomItem;
    return <EditRentRoomInfo>{
      indexId: getRandomId(),
      roomTypeId: roomItem.roomTypeId,
      roomTypeName: roomItem.roomTypeName,
      roomId: roomItem.roomId,
      roomNo: roomItem.roomNo,
      recNo,
      roomPriceList: (roomItem.subOrderRoomPriceCreateVOS || []).map((rentDay) => {
        const {
          rentDate,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          price,
          billFlag,
          marketStrategyPrice,
        } = rentDay;

        const rentDateText = format2DateStr(rentDate);
        rentDateIdMap[rentDate] = rentDateIdMap[rentDate] || getRandomId();
        originMarketPriceMap[`${recNo}|${rentDateText}`] = marketStrategyPrice;

        if (!billFlag) {
          // 没过夜审
          originCouponList.push(couponNo);
        }

        return {
          rentDateId: rentDateIdMap[rentDate],
          occType,
          rentDate: rentDateText,
          inputPrice: parseFen2Yuan(price),
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          couponValue: parseFen2Yuan(couponAmount),
          isBeforeAudit: !!billFlag, // 是否过夜审【是否抛账】
          priceChannel,
        };
      }),
    };
  });
  return {
    originBaseInfo: {
      resvNo,
      expectedArrivalTime,
      occDuration,
      expectedDepartureTime,
      sourceChannelId,
      sourceChannelName,
      priceChannel,
      priceStrategyId,
      priceStrategyName,
      channelOrderNo,
      remark,
      checkInRemark: checkinRemark,
    },
    originCouponList,
    originMarketPriceMap,
    commonInfo: {
      memberLevel,
      ownerWYId: wyuid,
    },
    rentTimeInfo: {
      occType,
      expectedArrivalTime: DateJS(expectedArrivalTime),
      rentTime: occDuration,
      expectedDepartureTime: DateJS(expectedDepartureTime),
      businessDate: DateJS(expectedArrivalTime).subtract(1, 'day').startOf('day'), // 默认预抵时间前一天,
    },
    channelInfo: {
      channelId: sourceChannelId,
      strategyId: priceStrategyId,
      channelOrderNo,
    },
    rentRoomInfos,
    remarkInfo: {
      remark,
      checkInRemark: checkinRemark,
    },
  };
};

export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const {
    originBaseInfo,
    commonInfo,
    rentTimeInfo,
    channelInfo,
    rentRoomInfos,
    remarkInfo,
    channelList,
    strategyList,
    roomPriceList,
    originMarketPriceMap,
  } = data;
  const { expectedArrivalTime, occType, rentTime, expectedDepartureTime } = rentTimeInfo;
  const { channelId, strategyId, channelOrderNo } = channelInfo;
  const sourceChannelName = findItemByKey(channelList, 'id', channelId)?.name;
  const priceStrategyName = findItemByKey(strategyList, 'id', strategyId)?.showName;
  const { cachePriceMap } = flatPriceList(roomPriceList);

  return {
    wyuId: commonInfo.ownerWYId,
    channelOrderNo,
    channelOrderNoOld: originBaseInfo.channelOrderNo,
    expectedArrivalTime: format2TimeStamp(expectedArrivalTime),
    expectedArrivalTimeOld: originBaseInfo.expectedArrivalTime,
    expectedDepartureTime: format2TimeStamp(expectedDepartureTime),
    expectedDepartureTimeOld: originBaseInfo.expectedDepartureTime,
    occDuration: rentTime,
    occDurationOld: originBaseInfo.occDuration,
    occType,
    // priceChannel: '',
    priceChannelOld: originBaseInfo.priceChannel,
    priceStrategyId: strategyId,
    priceStrategyIdOld: originBaseInfo.priceStrategyId,
    priceStrategyName,
    priceStrategyNameOld: originBaseInfo.priceStrategyName,
    remark: remarkInfo.remark,
    remarkOld: originBaseInfo.remark,
    checkinRemark: remarkInfo.checkInRemark,
    checkinRemarkOld: originBaseInfo.checkInRemark,
    resvNo: originBaseInfo.resvNo,
    sourceChannelId: channelId,
    sourceChannelIdOld: originBaseInfo.sourceChannelId,
    sourceChannelName,
    sourceChannelNameOld: originBaseInfo.sourceChannelName,
    subOrderCreateVOS: rentRoomInfos.map((room) => {
      const { recNo, roomId, roomNo, roomTypeId, roomTypeName } = room;
      const strategyPriceMap = cachePriceMap[roomTypeId] || {};
      return {
        deposit: null,
        recNo,
        roomId,
        roomNo,
        roomTypeId,
        roomTypeName,
        sourceChannelId: channelId,
        sourceChannelName,
        subOrderRoomPriceCreateVOS: (room.roomPriceList || []).map((dayInfo) => {
          const {
            rentDate,
            inputPrice,
            couponAmount,
            couponName,
            couponNo,
            couponType,
            couponWyuid,
            couponSubType,
            crsCouponAmount,
          } = dayInfo;
          const dateTime = format2TimeStamp(rentDate);

          return {
            couponAmount,
            couponName,
            couponNo,
            couponType,
            couponWyuid,
            couponSubType,
            crsCouponAmount,
            price: parseYuan2Fen(inputPrice),
            priceStrategyId: strategyId,
            rentDate: dateTime,
            strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
            marketStrategyPrice: originMarketPriceMap[`${recNo}|${rentDate}`],
          };
        }),
      };
    }),
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.ReserveEditor);
